import { useMutation } from "@apollo/client";
import { StoreOutlined } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { REQUEST_DEACTIVATE_COMPANY } from "graphql/company";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getGraphqlErrorMessage } from "utils/graphql";
import { Stack } from "components/layout/reusableLayouts";
import { ConfirmDialog, TextField, toast } from "@outpave/ds";

interface DeactivateAccountModalProps {
  openDeactivateAccountModal: boolean;
  setOpenDeactivateAccountModal: (open: boolean) => void;
  accountId?: string;
  accountName: string;
  handleUpdateAccount?: () => void;
}

export const DeactivateAccountModal: React.FC<DeactivateAccountModalProps> = ({
  openDeactivateAccountModal,
  setOpenDeactivateAccountModal,
  accountId,
  accountName,
  handleUpdateAccount,
}) => {
  const { t } = useTranslation();
  const [requestedReason, setRequestedReason] = useState("");

  // Each request starts with an empty reason.
  useEffect(() => {
    if (openDeactivateAccountModal) setRequestedReason("");
  }, [openDeactivateAccountModal]);

  const [deactivateCompany, { loading }] = useMutation(
    REQUEST_DEACTIVATE_COMPANY,
    {
      onCompleted: (data) => {
        toast.success(t("platform.requestDeactivateAccountSuccess"));
        handleUpdateAccount && handleUpdateAccount();
        setOpenDeactivateAccountModal(false);
      },
      onError: (error) => {
        const errorMessage = error?.graphQLErrors
          ? getGraphqlErrorMessage(error?.graphQLErrors)
          : t("Please try again!");
        toast.error(errorMessage);
      },
    },
  );

  return (
    <ConfirmDialog
      open={openDeactivateAccountModal}
      tone="danger"
      title={t("platform.deactivateAccount")}
      icon={<StoreOutlined />}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      // The dialog holds while the request runs, and stays open after a failure for a retry.
      busy={loading}
      onConfirm={() =>
        deactivateCompany({
          variables: {
            id: accountId,
            ...(requestedReason !== "" && { reason: requestedReason }),
          },
        })
      }
      // busy swallows only the backdrop; Escape and the title close still reach
      // onCancel, and would read as a cancel while the request carried on.
      onCancel={() => {
        if (loading) return;
        setOpenDeactivateAccountModal(false);
      }}
      message={
        <Stack gap="var(--space-4)">
          {/* One paragraph, or the column lays out each piece of the sentence as its own row. */}
          <Typography component="p" variant="inherit">
            <Trans
              i18nKey="platform.deactivateAccountConfirmMessage"
              components={{
                account_name: (
                  <Typography
                    component="span"
                    variant="inherit"
                    sx={{ color: "var(--color-text-brand)" }}
                  />
                ),
              }}
              values={{ accountName: accountName }}
            />
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={2}
            label={t("platform.reasonForRequest")}
            placeholder={t("platform.reason")}
            name="notes"
            value={requestedReason}
            onChange={(e) => setRequestedReason(e.target.value)}
            disabled={loading}
          />
        </Stack>
      }
    />
  );
};
